import { api } from '../core/client';
import { wirePhone } from '../core/wire';

/** A bill line as returned by the ThaliOS Web API. */
export interface ApiBillItem {
  name: string;
  cat: string;
  qty: number;
  rate: number;
  veg: boolean;
  /** This line's GST rate % — the menu item's own, else the outlet's. Optional: an older API omits it. */
  gst?: number;
  /** Whether `rate` already contains the GST. */
  taxInclusive?: boolean;
}

/** An open bill as returned by the ThaliOS Web API. */
export interface ApiOpenBill {
  table: string;
  meta: string;
  count: number;
  items: ApiBillItem[];
  /** The bill's effective rate — every line's rate when they agree, else the weighted rate.
   *  Totals are summed from the per-line `gst` values, not from this. */
  gstPct: number;
  /** True only when EVERY line is inclusive, so the counter shows no tax row. */
  taxInclusive?: boolean;
  /** Customer name / mobile on the order (both optional — blank for an anonymous walk-in). */
  customerName: string;
  customerPhone: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string | null;
  result: T;
}

/** A customer's table bill through its request → approve → pay lifecycle. */
export interface ApiCustomerBill {
  id: string;
  phone: string;
  table: string;
  /** requested | approved | paid. */
  status: 'requested' | 'approved' | 'paid';
  amount: number;
  approvedAmount: number;
  paymentMethod: string;
  requestedAt: string;
  approvedAt: string;
  paidAt: string;
}

/**
 * The payment mode as the API spells it. The counter's buttons are labelled "Cash" / "UPI" /
 * "Card" / "Wallet" / "Credit" and that label was posted verbatim, which the API rejected
 * ("Method must be cash, card, upi, wallet, online or bank") — so Settle failed on every mode,
 * for every bill. Normalised here, at the wire boundary, rather than in each screen.
 */
function wireMethod(method: string): string {
  return (method ?? '').trim().toLowerCase();
}


/** Billing API — open bills + the customer bill request → approve → pay lifecycle. */
export const billService = {
  open(): Promise<ApiOpenBill[]> {
    return api.get<ApiEnvelope<ApiOpenBill[]>>('/bill').then((r) => r.data.result);
  },

  /**
   * Settle a table's open bill at the counter — marks its orders paid + frees the table.
   *
   * `table` is the bill's key as {@link open} returned it: a floor id ("T7") for a dine-in
   * table, or the order number ("#0001") for a takeaway/delivery bill. Both are settleable.
   */
  settle(table: string, method: string): Promise<void> {
    return api.post('/bill/settle', { table, method: wireMethod(method) }).then(() => undefined);
  },

  /** Customer asks for their bill (status → requested). */
  request(phone: string, table: string, amount: number): Promise<ApiCustomerBill> {
    return api
      .post<ApiEnvelope<ApiCustomerBill>>('/bill/request', { phone: wirePhone(phone), table, amount })
      .then((r) => r.data.result);
  },

  /** The signed-in customer's live (unpaid) bill, or null when they have none. */
  mine(phone: string): Promise<ApiCustomerBill | null> {
    return api
      .get<ApiEnvelope<ApiCustomerBill | null>>('/bill/mine', { params: { phone: wirePhone(phone) } })
      .then((r) => r.data.result ?? null)
      .catch(() => null);
  },

  /** The panel approval queue — every not-yet-paid customer bill. */
  pending(): Promise<ApiCustomerBill[]> {
    return api.get<ApiEnvelope<ApiCustomerBill[]>>('/bill/requests').then((r) => r.data.result ?? []);
  },

  /** Waiter/admin confirms the amount (status → approved). */
  approve(id: string, amount: number): Promise<void> {
    return api.post(`/bill/${id}/approve`, { amount }).then(() => undefined);
  },

  /** Customer settles an approved bill (status → paid). */
  pay(id: string, method: string): Promise<void> {
    return api.post(`/bill/${id}/pay`, { method: wireMethod(method) }).then(() => undefined);
  }
};
